<template>
  <div
    class="max-w-xl w-full sm:rounded-lg sm:shadow-lg px-6 py-10
    flex flex-col items-center justify-center"
  >
    <div
      class="w-full text-left text-xs underline cursor-pointer"  
      @click="goBackToLogin"
    >
      {{ $t("message.back_to_login") }}
    </div>
    <Logo class="place-self-center mb-6" />
    <p
      class="sm:text-2xl text-xl mb-4"
      style="color: #000000;"
    >
      {{ $t("message.otp_page_title") }}
    </p>

    <div
      v-if="!otpTypeSelected"
      class="w-80 flex-col justify-center space-y-4"
    >
      <div class="flex flex-col justify-start space-y-2">
        <div class="flex flex-row items-center justify-start">
          <label
            class="mb-0 -mt-2 pt-3 pl-1 leading-tighter cursor-text align-middle text-xs items-start"
            :style="{ color: global.boxColor || '#696E79' }"
          >
            {{ $t("message.select_otp_type") }}
          </label>
        </div>
        <div class="flex flex-row items-center justify-between">
          <div class="relative inline-flex w-full items-center">
            <svg
              class="w-2 h-2 absolute top-0 right-0 m-5 pointer-events-none"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 412 232"
            >
              <path
                d="M206 171.144L42.678 7.822c-9.763-9.763-25.592-9.763-35.355 0-9.763 9.764-9.763 25.592 0 35.355l181 181c4.88 4.882 11.279 7.323 17.677 7.323s12.796-2.441 17.678-7.322l181-181c9.763-9.764 9.763-25.592 0-35.355-9.763-9.763-25.592-9.763-35.355 0L206 171.144z"
                fill="#648299"
                fill-rule="nonzero"
              />
            </svg>
            <select
              v-model="contact_value"
              class="border-2 rounded-lg
          pl-5 pr-10 bg-white
          focus:border-blue-900
            focus:outline-none
            appearance-none w-full h-12"
              :style="{
                borderColor: otpSelectBorderColor,
                color: global.boxColor || '#696E79'
              }"
              :aria-label="$t('message.select_otp_type')"
              aria-required="true"
              required
            >
              <option
                v-for="(option, index) in contacts"
                :key="index"
                :value="option.cont_value"
              >
                {{ getContactValue(option.cont_value) }}
              </option>
            </select>
          </div>
        </div>
        <div
          class="text-left pt-3 text-sm"
          :style="{ color: global.boxColor || '#696E79' }"
        >
          <span v-if="contact_value !== '' && getType() === 'email'">
            {{ $t("message.otp_send_mail") }}
            <b>{{ getContactValue(contact_value) }}</b>
          </span>
          <span v-if="contact_value !== '' && getType() === 'phone'">
            {{ $t("message.otp_send_phone") }}
            <b>{{ getContactValue(contact_value) }}</b>
          </span>
        </div>
      </div>
      <Button
        class="w-full"
        type="primary"
        :disabled="!enableConfirmAuthType"
        @onClick="onSendOtp"
      >
        {{ $t("message.confirm_password") }}
      </Button>
      <Message
        class="w-full"
        :type="'info'"
        :text="$t('message.unknown_contacts')"
        :style="{ color: global.boxColor || '#696E79' }"
      />
      <Message
        v-if="message && message.type && message.text"
        class="w-full"
        :type="message.type"
        :text="message.text"
      />
    </div>
    <div
      v-if="otpTypeSelected"
      class="w-80 flex-col justify-center space-y-4"
    >
      <div class="flex flex-col justify-start space-y-2">
        <div class="flex flex-row items-center justify-start">
          <label
            class="mb-0 -mt-2 pt-3 pl-1 leading-tighter cursor-text align-middle text-xs items-start"
            :style="{ color: global.boxColor || '#696E79' }"
          >
            {{ $t("message.insert_otp") + mapOtpTypeToLabel() }}
          </label>
        </div>
        <div class="flex flex-row items-center gap-2">
          <Input
            v-model="otp"
            class="w-full"
            type="text"
            autofocus
            required
            :placeholder="$t('message.otp_code')"
            :aria-label="$t('message.otp_code')"
            aria-required="true"
          />
          <Tooltip :text="$t('message.send_again')">
            <button
              id="btn"
              class="text-sm text-center uppercase disabled:opacity-60"
              @click="onSendOtp"
            >
              <RefreshIcon />
            </button>
          </Tooltip>
        </div>
        <div class="flex justify-between">
          <div>
            <p class="text-xs">
              {{ $t("message.otp_duration") }}
            </p>
          </div>
        </div>
      </div>

      <Button
        class="w-full"
        type="primary"
        :disabled="!enableConfirmButton"
        @onClick="onConfirmOtp"
      >
        {{ $t("message.confirm_password") }}
      </Button>
      <Message
        v-if="message && message.type && message.text"
        class="w-full"
        :type="message.type"
        :text="message.text"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Button from '@/components/atoms/Button.vue';
import Tooltip from '@/components/atoms/Tooltip.vue';
import RefreshIcon from '@/components/atoms/RefreshIcon.vue';
import Input from '@/components/atoms/Input.vue';
import Logo from '@/components/atoms/Logo.vue';
import Message from '@/components/atoms/Message.vue';
import { AlertMessage, MessageType } from '@/model/message';
import {
  refreshTokensAndRedirect,
  signOut
} from '@/service/cognitoService';
import { ContactType } from '@/service/otpService';
import {
  CRMContactType,
  confirmOtpRequest,
  getCrmContacts,
  sendOtpRequest
} from '@/service/otpService';
import { hexToRgba, maskString } from '@/utils';
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { cleanSessionStorage } from '@/plugins/session';
import { ROUTES_NAMES } from '@/constants';

export default defineComponent({
  name: 'OtpPanel',
  components: {
    Button,
    Input,
    Logo,
    Message,
    RefreshIcon,
    Tooltip
  },
  data() {
    return {
      otp: '',
      contact_value: '',
      contacts: [] as CRMContactType[],
      message: {} as AlertMessage | undefined,
      otpTypeSelected: false,
      otpCounter: 0,
      getContactValue(value: string): string {
        const contact = this.contacts.find(
          x => x.cont_value === value
        ) as CRMContactType;
        return maskString(value, contact?.cont_type);
      },
      getType(): ContactType {
        const contact = this.contacts.find(
          x => x.cont_value === this.contact_value
        ) as CRMContactType;
        return contact?.cont_type;
      }
    };
  },
  computed: {
    ...mapGetters([
      'global',
      'signInMessages',
      'signInUtils',
      'myEmail',
      'appStatus',
      'isIframe',
      'idToken',
      'accessToken',
      'refreshToken'
    ]),
    enableConfirmButton(): boolean {
      return this.otp !== '';
    },
    enableConfirmAuthType(): boolean {
      return this.contact_value !== '';
    },
    otpSelectBorderColor(): string {
      const boxColor = this.global.boxColor || '#696E79';
      return this.contact_value ? boxColor : hexToRgba(boxColor, 0.5);
    }
  },
  async mounted() {
    try {
      this.startLoading();
      this.contacts = await getCrmContacts();
      this.endLoading();
    } catch (error) {
      this.endLoading();
      this.message = {
        type: MessageType.Error,
        text: this.$t('message.get_user_contacts')
      };
    }
  },
  methods: {
    ...mapActions(['startLoading', 'endLoading']),
    async onConfirmOtp(): Promise<void> {
      try {
        this.message = undefined;
        this.startLoading();
        await confirmOtpRequest(this.otp.trim());
        this.otpCounter = 0;
        await refreshTokensAndRedirect(this.isIframe);
      } catch (error) {
        this.endLoading();
        this.message = {
          type: MessageType.Error,
          text: this.$t('message.confirm_otp_error')
        };
      }
    },
    async onSendOtp(): Promise<void> {
      try {
        this.message = undefined;
        this.startLoading();
        const contact = this.contacts.find(
          x => x.cont_value === this.contact_value
        ) as CRMContactType;
        await sendOtpRequest(contact);
        this.otpCounter++;
        this.otpTypeSelected = true;
        if(this.otpCounter > 3) {
          this.message = {
            type: MessageType.Info,
            text: this.$t('message.otp_support')
          };
        }
        this.endLoading();
      } catch (error) {
        this.endLoading();
        this.otpCounter = 0;
        this.message = {
          type: MessageType.Error,
          text: this.$t('message.send_otp_error')
        };
      }
    },
    mapOtpTypeToLabel(): string {
      const contact = this.contacts.find(
        x => x.cont_value === this.contact_value
      ) as CRMContactType;
      switch (contact?.cont_type) {
      case 'email':
        return this.$t('message.email');
      case 'phone':
        return this.$t('message.phone');
      default:
        return contact?.cont_type;
      }
    },
    async goBackToLogin(): Promise<void> {
      try {
        this.startLoading();
        await signOut();
        cleanSessionStorage();
        this.endLoading();
        this.$router.push({
          path: ROUTES_NAMES.login,
          query: {
            ...this.$route.query
          }
        });
      } catch (error) {
        this.endLoading();
      }
    }
  }
});
</script>
